import { isLeadIntakeOrigin, MAX_LEAD_INTAKE_ORIGINS, type LeadIntakeSettingsResponse, type SaveLeadIntakeSettingsBody } from "@gioia365/contracts";
import type { Translate } from "../../i18n/locale";

// La bozza di Impostazioni → «Ingressi dei Lead» (piano dell'ingresso del 10 ottobre 2026, I2, Task 4; IMP-2): le due porte accese o
// spente, i siti da cui arrivano i moduli e «Chi diventa owner» di GoHighLevel; le modifiche a parole per «Cosa cambia per il team»;
// il corpo del salvataggio con la versione letta di ogni porta; e il pezzo di codice del modulo del sito, costruito con le parole dei
// cataloghi. «Copia l'indirizzo», «Cambia l'indirizzo» e il Lead di prova non sono nella bozza: partono subito.

export type IntakeDraft = {
  web: { active: boolean; allowedOrigins: string[] };
  ghl: { active: boolean; ownerFrom: "tag" | "rule" };
};
export type IntakeEdits = { web?: Partial<IntakeDraft["web"]>; ghl?: Partial<IntakeDraft["ghl"]> };

/** Le porte come sono salvate. */
export function savedOf(base: LeadIntakeSettingsResponse): IntakeDraft {
  return {
    web: { active: base.web.active, allowedOrigins: [...base.web.allowedOrigins] },
    ghl: { active: base.ghl.active, ownerFrom: base.ghl.ownerFrom },
  };
}

/** Le porte salvate con sopra le modifiche. */
export function draftOf(saved: IntakeDraft, edits: IntakeEdits): IntakeDraft {
  return { web: { ...saved.web, ...edits.web }, ghl: { ...saved.ghl, ...edits.ghl } };
}

const sameList = (left: readonly string[], right: readonly string[]) => left.length === right.length && left.every((value, index) => value === right[index]);

/** «Cosa cambia per il team»: una frase per ogni cosa cambiata, nell'ordine della pagina. */
export function describeIntakeChanges(saved: IntakeDraft, draft: IntakeDraft, t: Translate): string[] {
  const changes: string[] = [];
  if (saved.web.active !== draft.web.active) changes.push(t(draft.web.active ? "leadIntakeSettings.changes.webOn" : "leadIntakeSettings.changes.webOff"));
  if (!sameList(saved.web.allowedOrigins, draft.web.allowedOrigins)) {
    changes.push(
      draft.web.allowedOrigins.length === 0
        ? t("leadIntakeSettings.changes.originsNone")
        : t("leadIntakeSettings.changes.origins", { list: draft.web.allowedOrigins.join(", ") }),
    );
  }
  if (saved.ghl.active !== draft.ghl.active) changes.push(t(draft.ghl.active ? "leadIntakeSettings.changes.ghlOn" : "leadIntakeSettings.changes.ghlOff"));
  if (saved.ghl.ownerFrom !== draft.ghl.ownerFrom) changes.push(t(draft.ghl.ownerFrom === "tag" ? "leadIntakeSettings.changes.ownerTag" : "leadIntakeSettings.changes.ownerRule"));
  return changes;
}

/** Il corpo di `PUT`: le due porte intere, con la versione letta di ognuna (`null` per una che non c'è ancora). */
export function saveBodyOf(base: LeadIntakeSettingsResponse, draft: IntakeDraft): SaveLeadIntakeSettingsBody {
  return {
    web: { active: draft.web.active, allowedOrigins: [...draft.web.allowedOrigins], expectedVersion: base.web.version },
    ghl: { active: draft.ghl.active, ownerFrom: draft.ghl.ownerFrom, expectedVersion: base.ghl.version },
  };
}

export type OriginProblem = "invalid" | "taken" | "max";

/**
 * Il sito scritto da chi imposta, portato all'origine che manda il browser: senza `https://` lo si aggiunge, un percorso o una `/` in
 * fondo si tolgono, il nome si scrive in minuscolo. `null` se non è l'indirizzo di un sito.
 */
export function normalizeOrigin(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === "" || /\s/.test(trimmed)) return null;
  try {
    const url = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    if (url.hostname === "" || url.username !== "" || url.password !== "") return null;
    return isLeadIntakeOrigin(url.origin) ? url.origin : null;
  } catch {
    return null;
  }
}

/** Il sito da aggiungere, oppure perché no. */
export function originToAdd(text: string, current: readonly string[]): { ok: true; origin: string } | { ok: false; problem: OriginProblem } {
  const origin = normalizeOrigin(text);
  if (origin === null) return { ok: false, problem: "invalid" };
  if (current.includes(origin)) return { ok: false, problem: "taken" };
  if (current.length >= MAX_LEAD_INTAKE_ORIGINS) return { ok: false, problem: "max" };
  return { ok: true, origin };
}

// ---------------------------------------------------------------------------------------------------------------
// Il pezzo di codice del modulo del sito

const HTML_ESCAPES: Readonly<Record<string, string>> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
const html = (text: string) => text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);
/** Un testo come stringa di JavaScript dentro `<script>`: senza `<`, che chiuderebbe lo script. */
const js = (text: string) => JSON.stringify(text).replace(/</g, "\\u003c");

/**
 * Il controllo del cellulare nel browser, prima dell'invio, come sorgente di JavaScript da mettere nel modulo (rilievo di Codex sulla
 * #167, accolto da Fable nel turno di notte dell'11 ottobre 2026): la porta risponde sempre allo stesso modo, e senza questo un fisso
 * o un numero incompleto veniva scartato in silenzio dopo il ringraziamento. È più largo della regola del server (`isMobile` di
 * `web/payload.ts`), così non ferma mai un numero che il server accetta: ferma soltanto meno di 8 cifre e, tolti `+` o `00` e poi
 * il `39`, un numero che comincia con `0` (un fisso italiano). Lo zero svizzero fra parentesi, `(0)`, si toglie prima, come fa il
 * server. Gli altri numeri che il server scarta restano scarti silenziosi.
 */
export const MOBILE_CHECK_SOURCE = [
  `function (value) {`,
  `    var text = String(value).replace(/\\(0\\)/g, "").replace(/[\\s.\\-\\/()]/g, "");`,
  `    if (text.replace(/\\D/g, "").length < 8) return false;`,
  `    var international = /^(\\+|00)/.test(text);`,
  `    var rest = text.replace(/^(\\+|00)/, "");`,
  `    if (international) rest = rest.replace(/^39/, "");`,
  `    return !/^0/.test(rest);`,
  `  }`,
].join("\n");

/**
 * Il modulo pronto da mettere nella pagina del sito o della landing (D3): i campi che la porta legge, il campo civetta `website`
 * nascosto a chi guarda, `t` con l'ora in cui la pagina si apre (e di nuovo dopo un invio), i parametri `utm_*` dell'indirizzo della
 * pagina e la pagina stessa senza la sua query (`page_url`: la query può portare dati di chi arriva). L'invio è un `fetch` con i campi
 * del modulo (`application/x-www-form-urlencoded`), che non chiede il permesso del CORS al browser; la risposta si legge soltanto
 * dalle origini scritte nel collegamento.
 */
export function snippetOf(address: string, t: Translate): string {
  const label = (key: Parameters<Translate>[0]) => html(t(key));
  return [
    `<form id="gioia365-modulo" novalidate>`,
    `  <label>${label("leadIntakeSettings.form.firstName")} <input name="first_name" autocomplete="given-name"></label>`,
    `  <label>${label("leadIntakeSettings.form.lastName")} <input name="last_name" autocomplete="family-name"></label>`,
    `  <label>${label("leadIntakeSettings.form.phone")} <input name="phone" type="tel" autocomplete="tel" required></label>`,
    `  <label>${label("leadIntakeSettings.form.email")} <input name="email" type="email" autocomplete="email"></label>`,
    `  <label>${label("leadIntakeSettings.form.salon")} <input name="salon" autocomplete="organization"></label>`,
    `  <label>${label("leadIntakeSettings.form.city")} <input name="city" autocomplete="address-level2"></label>`,
    `  <label><input name="consent" type="checkbox"> ${label("leadIntakeSettings.form.consent")}</label>`,
    `  <div style="position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden" aria-hidden="true">`,
    `    <input name="website" tabindex="-1" autocomplete="off">`,
    `  </div>`,
    `  <input type="hidden" name="t">`,
    `  <input type="hidden" name="form_id" value="modulo-sito">`,
    `  <button type="submit">${label("leadIntakeSettings.form.send")}</button>`,
    `  <p data-gioia365-esito role="status"></p>`,
    `</form>`,
    `<script>`,
    `(function () {`,
    `  var form = document.getElementById("gioia365-modulo");`,
    `  var esito = form.querySelector("[data-gioia365-esito]");`,
    `  var query = new URLSearchParams(window.location.search);`,
    `  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach(function (name) {`,
    `    var value = query.get(name);`,
    `    if (!value) return;`,
    `    var input = document.createElement("input");`,
    `    input.type = "hidden"; input.name = name; input.value = value;`,
    `    form.appendChild(input);`,
    `  });`,
    `  form.elements.t.value = String(Date.now());`,
    `  var looksLikeMobile = ${MOBILE_CHECK_SOURCE};`,
    `  form.addEventListener("submit", function (event) {`,
    `    event.preventDefault();`,
    `    if (!looksLikeMobile(form.elements.phone.value)) { esito.textContent = ${js(t("leadIntakeSettings.form.phoneInvalid"))}; form.elements.phone.focus(); return; }`,
    `    var data = new URLSearchParams(new FormData(form));`,
    `    data.set("page_url", window.location.origin + window.location.pathname);`,
    `    fetch(${js(address)}, { method: "POST", body: data })`,
    `      .then(function (response) {`,
    `        if (response.ok) { form.reset(); form.elements.t.value = String(Date.now()); esito.textContent = ${js(t("leadIntakeSettings.form.thanks"))}; }`,
    `        else esito.textContent = response.status === 429 ? ${js(t("leadIntakeSettings.form.tooMany"))} : ${js(t("leadIntakeSettings.form.error"))};`,
    `      })`,
    `      .catch(function () { esito.textContent = ${js(t("leadIntakeSettings.form.error"))}; });`,
    `  });`,
    `})();`,
    `</script>`,
  ].join("\n");
}
